import { useCallback, useEffect, useState } from 'react'
import { Button, Card, CardHeader, useLocale } from '@hk/ui'
import {
  addShift, fetchShifts, formatCount, formatQuantity, saveShift, setShiftActive,
  translateError, type ShiftRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

const suggestCode = (name: string) =>
  name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 24)

/**
 * **MINUTES PAST MIDNIGHT ↔ `HH:MM`.** The wire carries minutes because a shift is a
 * position in the hotel's own day; the box carries `HH:MM` because that is what a person
 * types. The conversion lives here, once, rather than in three handlers.
 *
 * `toMinutes` returns null for anything it cannot read, and null means "do not save" —
 * coercing `9:xx` to 540 would silently record a time nobody typed.
 */
export function toMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const hours = Number(m[1])
  const mins = Number(m[2])
  if (hours > 23 || mins > 59) return null
  return hours * 60 + mins
}

export function toClock(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/* ============================================================================
   W-129's LAST CARD — WHEN THIS HOTEL WORKS.  Sprint 80.

   ── THE GAP ────────────────────────────────────────────────────────────────────
   Two shifts were seeded — Morning 08:00–14:00, Evening 14:00–20:00 — and a hotel had no
   way to say anything else. A hotel running three, or starting at six, could only be
   changed by a developer editing a file, which is not a hotel configuring itself.

   ── AND THE NUMBER THIS CARD MOVES IS NOT A LABEL ──────────────────────────────
   **A SHIFT'S LENGTH IS THE CREDIT BUDGET OF EVERY LANE ON THE ASSIGNMENT BOARD.** That is
   defect #125: there used to be a hand-typed budget column, it was seeded at 8.0, a credit
   became twenty minutes, and every lane went over budget at the sixth room every morning
   until the column was deleted and the figure derived from the hours instead.

   So the budget is printed beside the hours, on every row, as it is typed. Changing
   08:00–14:00 to 08:00–12:00 takes a third off what every attendant is expected to carry,
   and a card that showed only the times would hide its own consequence.
   ========================================================================= */

export function ShiftsCard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [nameAr, setNameAr] = useState('')
  const [start, setStart] = useState('08:00')
  const [end, setEnd] = useState('14:00')
  const [refusal, setRefusal] = useState<string>()

  /* **PLAIN STATE, NOT `useQuery`, AND THAT IS THE HOST SCREEN'S CHOICE NOT A WHIM.**
     W-129 loads itself with `useState` and a `useCallback`, and its tests render it with
     no `QueryClientProvider` — correctly, because that is how the application mounts it.
     A card using react-query threw "No QueryClient set" through fifteen of the
     configuration screen's own tests the moment it was added. A component belongs to the
     screen it is on. */
  const [rows, setRows] = useState<ShiftRow[] | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      setRows(await fetchShifts(propertyId))
    } catch {
      /* Silent: this is one card on a screen that has others, and a hotel whose shifts
         cannot be read still has a name and a time zone to correct. */
      setRows(null)
    }
  }, [propertyId])

  useEffect(() => { void load() }, [load])

  const startMinutes = toMinutes(start)
  const endMinutes = toMinutes(end)

  const act = async (work: () => Promise<ShiftRow[]>) => {
    setBusy(true)
    try {
      setRows(await work())
      setRefusal(undefined)
    } catch (e) {
      setRefusal(String(translateError(e, locale).message))
    } finally {
      setBusy(false)
    }
  }

  const add = () => act(async () => {
    const next = await addShift(propertyId, {
      code: suggestCode(name), name: name.trim(), nameAr: nameAr.trim() || null,
      startMinuteLocal: startMinutes ?? 0, endMinuteLocal: endMinutes ?? 0,
    })
    setAdding(false); setName(''); setNameAr('')
    return next
  })

  const save = (input: {
    id: string; name: string; nameAr: string | null
    startMinuteLocal: number; endMinuteLocal: number
  }) => act(() => saveShift(propertyId, input.id, {
    name: input.name, nameAr: input.nameAr,
    startMinuteLocal: input.startMinuteLocal, endMinuteLocal: input.endMinuteLocal,
  }))

  const setActive = (input: { id: string; isActive: boolean }) =>
    act(() => setShiftActive(propertyId, input.id, input.isActive))

  if (rows === null) return null

  return (
    <Card>
      <CardHeader title={t.shTitle} />
      <p className="max-w-[70ch] px-3.5 pt-0 text-[12.5px] leading-relaxed text-muted-foreground">
        {t.shIntro}
      </p>

      {refusal && (
        <p role="alert" className="px-3.5 text-[12.5px]"
           style={{ color: 'var(--hk-hk-dirty-text)' }}>
          {refusal}
        </p>
      )}

      <div className="overflow-x-auto p-3.5">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="p-2 text-start font-medium">{t.shName}</th>
              <th className="p-2 text-start font-medium">{t.shNameAr}</th>
              <th className="p-2 text-start font-medium">{t.shStarts}</th>
              <th className="p-2 text-start font-medium">{t.shEnds}</th>
              <th className="p-2 text-end font-medium">{t.shBudget}</th>
              <th className="p-2 text-end font-medium sr-only">{t.cfgSave}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <ShiftLine
                key={row.shiftId}
                row={row}
                busy={busy}
                onSave={(next) => void save({ id: row.shiftId, ...next })}
                onSetActive={(isActive) => void setActive({ id: row.shiftId, isActive })}
              />
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-3.5 pb-3.5">
        {adding ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[12px]">
              {t.shName}
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-label={t.shName}
                className="w-40 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px]">
              {t.shNameAr}
              <input
                value={nameAr}
                onChange={(e) => setNameAr(e.target.value)}
                dir="rtl"
                aria-label={t.shNameAr}
                className="w-40 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px]">
              {t.shStarts}
              <input
                value={start}
                onChange={(e) => setStart(e.target.value)}
                dir="ltr"
                aria-label={t.shStarts}
                className="tnum w-24 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px]">
              {t.shEnds}
              <input
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                dir="ltr"
                aria-label={t.shEnds}
                className="tnum w-24 rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              />
            </label>

            <Button
              type="button"
              size="sm"
              disabled={
                name.trim() === '' || startMinutes === null || endMinutes === null
                || busy
              }
              onClick={() => void add()}
            >
              {t.cfgSave}
            </Button>
            <Button type="button" size="sm" variant="ghost"
                    onClick={() => { setAdding(false); setName(''); setNameAr('') }}>
              {t.cancel}
            </Button>

            {/* **A TIME THAT CANNOT BE READ IS NOT COERCED.** `9:xx` becoming 09:00 would
                record an hour nobody typed, on the field that sets everybody's budget. */}
            {(startMinutes === null || endMinutes === null) && (
              <span className="text-[12px]" style={{ color: 'var(--hk-hk-dirty-text)' }}>
                {t.shClockShape}
              </span>
            )}
          </div>
        ) : (
          <Button type="button" size="sm" variant="outline" onClick={() => setAdding(true)}>
            {t.shAdd}
          </Button>
        )}
      </div>
    </Card>
  )
}

function ShiftLine({
  row, busy, onSave, onSetActive,
}: {
  row: ShiftRow
  busy: boolean
  onSave: (next: {
    name: string; nameAr: string | null; startMinuteLocal: number; endMinuteLocal: number
  }) => void
  onSetActive: (isActive: boolean) => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [name, setName] = useState(row.name)
  const [nameAr, setNameAr] = useState(row.nameAr ?? '')
  const [start, setStart] = useState(toClock(row.startMinuteLocal))
  const [end, setEnd] = useState(toClock(row.endMinuteLocal))

  const startMinutes = toMinutes(start)
  const endMinutes = toMinutes(end)
  const readable = startMinutes !== null && endMinutes !== null

  const dirty =
    name.trim() !== row.name
    || nameAr.trim() !== (row.nameAr ?? '')
    || startMinutes !== row.startMinuteLocal
    || endMinutes !== row.endMinuteLocal

  /* **THE BUDGET AS IT WILL BE, NOT AS IT IS.** The whole point of printing it here is
     that somebody typing 12:00 in place of 14:00 sees the lane shrink before they save.
     Worked out with the hotel's own divisor, which the row already carries as a pair. */
  const perMinute = row.lengthMinutes > 0 ? row.creditBudget / row.lengthMinutes : 0
  const wouldBe = readable
    ? perMinute * (((endMinutes! - startMinutes!) % 1440 + 1440) % 1440 || 1440)
    : row.creditBudget

  return (
    <tr className={`border-b border-border/60 last:border-0 ${row.isActive ? '' : 'opacity-60'}`}>
      <td className="p-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label={`${row.code} — ${t.shName}`}
          className="w-36 rounded-hk border border-border bg-background px-2 py-1 text-[12.5px]"
        />
        <span className="mt-0.5 block text-[11px] text-muted-foreground">
          <span dir="ltr">{row.code}</span>
          {row.rosteredEver > 0 && <>{' · '}{fill(t.shRostered, {
            count: formatCount(row.rosteredEver, locale),
          })}</>}
          {!row.isActive && <>{' · '}{t.shRetired}</>}
        </span>
      </td>

      <td className="p-2">
        <input
          value={nameAr}
          onChange={(e) => setNameAr(e.target.value)}
          dir="rtl"
          aria-label={`${row.code} — ${t.shNameAr}`}
          className="w-36 rounded-hk border border-border bg-background px-2 py-1 text-[12.5px]"
        />
      </td>

      <td className="p-2">
        <input
          value={start}
          onChange={(e) => setStart(e.target.value)}
          dir="ltr"
          aria-label={`${row.code} — ${t.shStarts}`}
          className="tnum w-20 rounded-hk border border-border bg-background px-2 py-1 text-[12.5px]"
        />
      </td>

      <td className="p-2">
        <input
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          dir="ltr"
          aria-label={`${row.code} — ${t.shEnds}`}
          className="tnum w-20 rounded-hk border border-border bg-background px-2 py-1 text-[12.5px]"
        />
      </td>

      <td className="p-2 text-end tnum">
        {formatQuantity(wouldBe, locale)}
        {/* Said only when it is about to change, so the column is not noise on every row. */}
        {readable && dirty && wouldBe !== row.creditBudget && (
          <span className="block text-[11px]" style={{ color: 'var(--hk-hk-prog-text)' }}>
            {fill(t.shWas, { credits: formatQuantity(row.creditBudget, locale) })}
          </span>
        )}
      </td>

      <td className="p-2">
        <div className="flex items-center justify-end gap-1.5">
          {dirty && (
            <Button
              type="button"
              size="sm"
              disabled={busy || name.trim() === '' || !readable}
              onClick={() => onSave({
                name: name.trim(),
                nameAr: nameAr.trim() || null,
                startMinuteLocal: startMinutes!,
                endMinuteLocal: endMinutes!,
              })}
            >
              {t.cfgSave}
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" disabled={busy}
                  onClick={() => onSetActive(!row.isActive)}>
            {row.isActive ? t.shRetire : t.shRestore}
          </Button>
        </div>
      </td>
    </tr>
  )
}
